import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/apiClient';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Box, Nfc, AlertCircle } from 'lucide-react';

interface Mold {
  id: string;
  name: string;
  code: string | null;
  nfc_tag_uid: string | null;
  status: string;
  is_active: boolean;
}

interface MoldSelectorProps {
  value: string;
  onChange: (moldId: string, mold: Mold | null) => void;
  required?: boolean;
  disabled?: boolean;
}

export function MoldSelector({ value, onChange, required, disabled }: MoldSelectorProps) {
  // Fetch available molds (active and not currently in use)
  const { data: molds, isLoading } = useQuery({
    queryKey: ['molds-available'],
    queryFn: async () => {
      const { data, error } = await api
        .from('molds')
        .select('id, name, code, nfc_tag_uid, status, is_active')
        .eq('is_active', true)
        .in('status', ['available', 'active', null])
        .order('name', { ascending: true });
      if (error) throw error;
      return (data || []) as Mold[];
    },
  });

  const selectedMold = molds?.find((m) => m.id === value);

  const handleChange = (moldId: string) => {
    if (moldId === 'none') {
      onChange('', null);
    } else {
      const mold = molds?.find((m) => m.id === moldId) || null;
      onChange(moldId, mold);
    }
  };

  return (
    <div className="space-y-2">
      <Label className="flex items-center gap-2">
        <Box className="w-4 h-4" />
        Molde {required && '*'}
      </Label>

      <Select
        value={value || 'none'}
        onValueChange={handleChange}
        disabled={disabled || isLoading}
      >
        <SelectTrigger>
          <SelectValue placeholder={isLoading ? 'Cargando moldes...' : 'Seleccionar molde'} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="none">Sin molde</SelectItem>
          {molds?.map((mold) => (
            <SelectItem key={mold.id} value={mold.id}>
              <div className="flex items-center gap-2">
                <span>{mold.name}</span>
                {mold.code && (
                  <span className="text-muted-foreground">({mold.code})</span>
                )}
                {mold.nfc_tag_uid && (
                  <Nfc className="w-3 h-3 text-blue-500" />
                )}
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Show selected mold info */}
      {selectedMold && (
        <div className="flex items-center gap-2 text-sm">
          {selectedMold.nfc_tag_uid ? (
            <Badge variant="secondary" className="font-mono text-xs">
              <Nfc className="w-3 h-3 mr-1" />
              {selectedMold.nfc_tag_uid}
            </Badge>
          ) : (
            <Badge variant="outline" className="text-yellow-600">
              <AlertCircle className="w-3 h-3 mr-1" />
              Sin NFC asignado
            </Badge>
          )}
        </div>
      )}

      {required && !selectedMold?.nfc_tag_uid && selectedMold && (
        <p className="text-xs text-yellow-600">
          Este molde no tiene NFC asignado. El operador no podrá validar con escaneo.
        </p>
      )}
    </div>
  );
}
